import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { EFeatureFlags } from 'constants/features';
import { useDebounce } from 'hooks';
import { useIsFeatureEnabled } from 'hooks/useFeatures';
import { useInfinitePagination } from 'hooks/usePagination';
import useSubscriptionPermissions from 'hooks/useSubscriptionPermissions';
import _compact from 'lodash/compact';
import _filter from 'lodash/filter';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _reject from 'lodash/reject';
import { NOTIFICATION } from 'modules/alert/constants';
import {
  showErrorMessage,
  showSuccessMessage,
  showUncaughtErrorMessage,
} from 'modules/alert/utils';
import {
  useAddContributorsMutation,
  useAddExternalContributorsMutation,
  useCheckInviteContributorPermissionMutation,
  useGetContributorsQuery,
  useGetInvitesQuery,
} from 'modules/contributors/contributorsApi';
import {
  ICheckInviteContributorMutation,
  IContributor,
  IContributorCreate,
  IExternalContributorsCreate,
} from 'modules/contributors/types';
import { useModal } from 'modules/modals/ModalProvider';
import { useGetThreadQuery } from 'modules/threads/threadsApi';
import UserCard from 'modules/user/components/UserCard';
import { useGetUsersQuery } from 'modules/user/userApi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import colors from 'theme/colors';
import { IUser } from 'types/user';
import { Divider, Group, Icon, Spinner, Stack, Text, Title } from 'ui';
import { extractErrorMessagesFromResponse } from 'utils/helpers';

interface IProps {
  threadId: number;
}

interface IValidationError {
  status: number;
  data: {
    non_field_errors: string[];
  }[];
}

const AddContributors: React.FC<IProps> = ({ threadId }) => {
  const { next: loadNext, limit, offset } = useInfinitePagination({ pageSize: 25 });
  const [addContributors, { isLoading }] = useAddContributorsMutation();
  const isNautilusIntegration = useIsFeatureEnabled(EFeatureFlags.NAUTILUS_INTEGRATION);
  const [checkInviteContributorsPermission, { isLoading: isChecking }] =
    useCheckInviteContributorPermissionMutation();
  const [addExternalContributors] = useAddExternalContributorsMutation();

  const stickySelectedUsersListRef = useRef<HTMLDivElement>(null);
  const [enteredContributorInputQuery, setEnteredContributorInputQuery] = useState<string>('');
  const [showStickySelectedWorkspaceUsers, setShowStickySelectedWorkspaceUsers] = useState(false);
  const [workspaceUsersList, setWorkspaceUsersList] = useState<IUser[] | []>([]);
  const [selectedWorkspaceUsers, setSelectedWorkspaceUsers] = useState<IUser[] | []>([]);
  const [selectedExternalUsers, setSelectedExternalUsers] = useState<
    IExternalContributorsCreate[] | []
  >([]);

  const debouncedUserSearchQuery = useDebounce(enteredContributorInputQuery, 500);
  const { handleSubscriptionPermissionErrors } = useSubscriptionPermissions();
  const { close } = useModal();

  const { data: existingContributors } = useGetContributorsQuery({ id: String(threadId) });
  const { data: existingExternalContributors } = useGetInvitesQuery({ id: threadId });
  const { data: threadData } = useGetThreadQuery({ id: threadId });
  const {
    data: usersResponse,
    isLoading: isUsersLoading,
    isFetching: isFetchingUsers,
  } = useGetUsersQuery({
    limit,
    offset,
    search: debouncedUserSearchQuery,
  });
  const users = usersResponse?.results || [];

  const { handleSubmit } = useForm<IContributorCreate[]>({
    mode: 'onSubmit',
    shouldFocusError: false,
  });

  const uninvitedUsers: IUser[] = useMemo(() => {
    const existingContributorIds = [
      ...(_map(existingContributors?.results, 'user.id') ?? []),
      ..._map(selectedWorkspaceUsers, 'id'),
    ];
    return _filter(
      users,
      (user) => ![...existingContributorIds, threadData?.creator.id].includes(user.id)
    );
  }, [users, existingContributors, selectedWorkspaceUsers]);

  useEffect(() => setWorkspaceUsersList(uninvitedUsers), [uninvitedUsers, selectedWorkspaceUsers]);

  /** Transformed selected (non-external) users in format of internal contributor schema. */
  const selectedWorkspaceUserContributors: IContributorCreate[] = useMemo(() => {
    return _compact(selectedWorkspaceUsers).map((contributor) => ({
      user: contributor.id,
      thread: threadId,
    }));
  }, [selectedWorkspaceUsers]);

  const handleContributorInvitePermissionCheck = async (
    contributorsData: IContributor[],
    closeModalOnFinish = true
  ) => {
    const invitedContributorsUserId: ICheckInviteContributorMutation = {
      userIds: contributorsData.map((contributor) => contributor.user.id),
      threadId,
    };
    try {
      await checkInviteContributorsPermission(invitedContributorsUserId).unwrap();
      showSuccessMessage(NOTIFICATION.CONTRIBUTOR_CREATED);
      if (closeModalOnFinish) close();
    } catch (responseError) {
      handleSubscriptionPermissionErrors(
        responseError,
        'Can not check invite contributors permission. Please try again.'
      );
    }
  };

  const handleAddContributors = async (
    workspaceUsers: IContributorCreate[],
    closeModalOnFinish = true
  ) => {
    try {
      const existingUsersRes = await addContributors(workspaceUsers).unwrap();
      handleContributorInvitePermissionCheck(existingUsersRes, closeModalOnFinish);
    } catch (addContributorError) {
      (addContributorError as IValidationError).data.forEach((errorData) => {
        errorData.non_field_errors.forEach((nonFieldError: string) => {
          showErrorMessage(nonFieldError);
        });
      });
    }
  };

  const onSubmit = async () => {
    if (selectedWorkspaceUserContributors.length) {
      await handleAddContributors(selectedWorkspaceUserContributors);
    }

    if (selectedExternalUsers.length) {
      try {
        await addExternalContributors(selectedExternalUsers).unwrap();
        showSuccessMessage('External contributors added successfully');
        close();
      } catch (errorData) {
        const errorMessages = extractErrorMessagesFromResponse(errorData);
        if (errorMessages.length) {
          errorMessages.forEach((message) => {
            showErrorMessage(message);
          });
        } else {
          showUncaughtErrorMessage(errorData);
        }
      }
    }
  };

  const stickySelectedThreadsHeader = useMemo(() => {
    const stickyClasses = 'left-0 z-[99] w-full px-6 pb-2';
    return (
      <div
        role="button"
        onClick={() => stickySelectedUsersListRef.current?.scroll({ top: 0 })}
        onKeyDown={() => null} // Required for accessibility/linting.
        tabIndex={0}
        className={`text-lg ${showStickySelectedWorkspaceUsers ? stickyClasses : ''}`}
        style={
          showStickySelectedWorkspaceUsers
            ? { position: 'fixed', background: colors.dark1, color: colors.gray2 }
            : { position: 'static', color: colors.gray2 }
        }
        data-cy="modal-add-contributor-form-title"
      >
        Selected Users ({selectedWorkspaceUsers.length + selectedExternalUsers.length})
      </div>
    );
  }, [selectedWorkspaceUsers, selectedExternalUsers, showStickySelectedWorkspaceUsers]);

  const renderSelectedUsersList = useCallback(() => {
    const renderRemoveIcon = (onClick: () => void) => (
      <div>
        <Icon onClick={onClick} icon="RemoveUserIcon" />
      </div>
    );
    const selectedWorkspaceUsersList = selectedWorkspaceUsers.map((user) => (
      <UserCard
        user={user as IUser}
        showUserEmail
        trailingComponent={renderRemoveIcon(() =>
          setSelectedWorkspaceUsers(_reject(selectedWorkspaceUsers, { id: user.id }))
        )}
      />
    ));
    const externalUsersList = selectedExternalUsers.map((externalUser) => (
      <UserCard
        email={externalUser.email}
        subtitleComponent={
          <Text className="italic" color={colors.gray1} size="xs">
            External
          </Text>
        }
        trailingComponent={renderRemoveIcon(() =>
          setSelectedExternalUsers(_reject(selectedExternalUsers, { email: externalUser.email }))
        )}
      />
    ));

    return [selectedWorkspaceUsersList, externalUsersList];
  }, [selectedWorkspaceUsers, selectedExternalUsers]);

  const isUserAlreadySelected = (selectedUser: IUser | string) => {
    const emailToCheck = typeof selectedUser === 'string' ? selectedUser : selectedUser.email;
    return [...selectedExternalUsers, ...selectedWorkspaceUsers].some(
      (user) => user.email === emailToCheck
    );
  };

  const validateExternalUserEmail = (onSuccess: (value?: unknown) => void) => {
    const existingThreadContributorEmails =
      existingContributors?.results.map((contributor) => contributor.user.email) ?? [];
    const existingExternalContributorEmails = _map(existingExternalContributors?.results, 'email');

    if (
      [
        ...existingThreadContributorEmails,
        ...existingExternalContributorEmails,
        threadData?.creator.email,
      ].includes(enteredContributorInputQuery)
    ) {
      showErrorMessage('This user is already an existing contributor.');
      return;
    }

    if (isUserAlreadySelected(enteredContributorInputQuery)) {
      showErrorMessage('This user has already been added');
      return;
    }

    onSuccess(enteredContributorInputQuery);
  };

  const handleAddExternalUser = (email: string) => {
    const newExternalUser: IExternalContributorsCreate = { email, thread: [threadId] };
    setSelectedExternalUsers((previous) => [...previous, newExternalUser]);
  };

  if (isUsersLoading) {
    return (
      <Stack align="center" justify="center">
        <Spinner size="medium" />
      </Stack>
    );
  }

  const placeholder = isNautilusIntegration ? 'Search' : 'Search | invite users by email';

  const addWorkspaceUser = (user: IUser) => {
    if (isUserAlreadySelected(user)) {
      showErrorMessage('This user has already been added');
      return;
    }

    setSelectedWorkspaceUsers([...selectedWorkspaceUsers, user]);
  };

  return (
    <Stack gap="24px" fullHeight data-cy="modal-add-contributor-stack">
      <Title heading="h6" color={colors.gray2} cypressAttribute="modal-add-contributor-title">
        Search Users
      </Title>
      <div className="flex items-center gap-3">
        <Input
          id="contributors-select"
          type="search"
          onChange={(event) => setEnteredContributorInputQuery(event.target.value)}
          placeholder={placeholder}
          onKeyDown={(event) =>
            event.key === 'Enter' &&
            validateExternalUserEmail(() => handleAddExternalUser(enteredContributorInputQuery))
          }
        />
        <Button
          disabled={_isEmpty(enteredContributorInputQuery)}
          onClick={() =>
            validateExternalUserEmail(() => handleAddExternalUser(enteredContributorInputQuery))
          }
        >
          Invite
        </Button>
      </div>
      <Divider color={colors.blue3} />
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{
          height: '100%',
          gap: '24px',
          display: 'flex',
          overflow: 'hidden',
          flexDirection: 'column',
        }}
        data-cy="modal-add-contributor-form"
      >
        <Stack
          ref={stickySelectedUsersListRef}
          onScroll={(event) =>
            setShowStickySelectedWorkspaceUsers((event.target as HTMLElement).scrollTop > 50)
          }
          gap="24px"
          className="h-full pt-2"
          style={{ overflowY: 'auto' }}
        >
          <Stack gap="12px" data-cy="modal-add-related-thread-stack">
            {stickySelectedThreadsHeader}
            {renderSelectedUsersList()}
          </Stack>
          <Divider color={colors.blue3} />

          <Title heading="h6" color={colors.gray2} cypressAttribute="modal-add-contributor-title">
            Users in Your Workspace
          </Title>
          <Stack gap="12px">
            {workspaceUsersList.length ? (
              _map(workspaceUsersList, (user) => (
                <UserCard
                  showUserEmail
                  user={user}
                  trailingComponent={
                    <div>
                      <Icon onClick={() => addWorkspaceUser(user)} icon="AddUserIcon" />
                    </div>
                  }
                />
              ))
            ) : (
              <Text color={colors.gray1} size="sm">
                No uninvited users.
              </Text>
            )}
          </Stack>
        </Stack>
        {!!usersResponse?.next && (
          <Button
            variant="secondary"
            className="self-center"
            disabled={isUsersLoading || isFetchingUsers}
            type="button"
            onClick={loadNext}
          >
            {isFetchingUsers ? 'Loading...' : 'Load More'}
          </Button>
        )}
        <Group justify="flex-end" gap="15px" style={{ marginTop: 'auto' }}>
          <Button
            variant="secondary"
            onClick={close}
            color={colors.dark2}
            data-cy="modal-add-contributor-close-btn"
          >
            Close
          </Button>
          <Button type="submit" disabled={isLoading} data-cy="modal-add-contributor-save-btn">
            Save
          </Button>
        </Group>
      </form>
    </Stack>
  );
};

export default AddContributors;
